// @ts-nocheck

import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import Separator from 'flarum/common/components/Separator';
import username from 'flarum/common/helpers/username';
import avatar from 'flarum/common/helpers/avatar';
import User from 'flarum/common/models/User';
import extractText from 'flarum/common/utils/extractText';
import app from 'flarum/forum/app';

import type Mithril from 'mithril';
import Stream from 'flarum/common/utils/Stream';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

interface IAttrs {}

interface IState {
	currentData: User[];
	value: Stream<string>;
	searchQuery: Stream<string>;
	lastSearchedQuery: string;
	loading: boolean;
	timeoutKey: number | null;
}

const DEBOUNCE_TIME = 250;

export default class UserAutocompleteDropdown extends Component<IAttrs, IState> {
	oninit(vnode: Mithril.Vnode<IAttrs, this>): void {
		super.oninit(vnode);

		this.state = {
			currentData: [],
			value: Stream(''),
			searchQuery: Stream(''),
			lastSearchedQuery: '',
			loading: false,
			timeoutKey: null,
		};
	}

	view() {
		this.performSearch(this.state.searchQuery());

		let content = [];

		if (this.state.loading) {
			content.push(<Separator />, <LoadingIndicator />);
		} else if (this.state.searchQuery().length < this.minSearchLength()) {
			this.state.lastSearchedQuery = '';
		} else if (!this.state.currentData?.length) {
			content.push(
				<Separator />,
				<span>{extractText(app.translator.trans('glowingblue-author-filter.forum.index_page.filter_user.no_results'))}</span>
			);
		} else {
			content.push(
				<Separator />,
				this.state.currentData?.map((user) => (
					<Button
						class="BlomstraUserFilter-item Button"
						onclick={() => {
							this.handleUserChange(user);
						}}
					>
						{avatar(user)} {username(user)}
					</Button>
				))
			);
		}

		if (app.search.params().author) {
			// if author is set
			content.push(
				<Separator />,
				<Button class="Button" icon="fas fa-times" onclick={() => this.handleUserChange(null)}>
					{extractText(app.translator.trans('glowingblue-author-filter.forum.index_page.filter_user.remove_filter'))}
				</Button>
			);
		}

		return (
			<Dropdown
				buttonClassName="Button"
				label={this.label}
				updateOnClose
				accessibleToggleLabel={app.translator.trans('glowingblue-author-filter.forum.index_page.filter_user.accessible_label')}
				onshow={() => {
					$('input').focus();
				}}
			>
				<input
					type="text"
					class="FormControl"
					placeholder={extractText(app.translator.trans('glowingblue-author-filter.forum.index_page.filter_user.search_label'))}
					value={this.state.value()}
					oninput={(e: InputEvent) => {
						this.state.value(e.currentTarget!.value);

						this.state.timeoutKey && clearTimeout(this.state.timeoutKey);
						this.state.timeoutKey = setTimeout(() => {
							this.state.searchQuery(e.target!.value);
							m.redraw();
						}, DEBOUNCE_TIME);
					}}
				/>

				{content}
			</Dropdown>
		);
	}

	protected minSearchLength(): number {
		const val = app.forum.attribute<number>('authorFilterMinSearchLength');

		return val > 0 ? val : 3;
	}

	protected maxResults(): number {
		const val = app.forum.attribute<number>('authorFilterMaxResults');

		return val > 0 ? val : 5;
	}

	async performSearch(query: string): Promise<void> {
		if (this.state.lastSearchedQuery === query) return;

		if (this.state.searchQuery().length < this.minSearchLength()) {
			this.state.currentData = [];
			return;
		}

		this.state.loading = true;
		this.state.lastSearchedQuery = query;
		m.redraw();

		const data = await app.store.find<User[]>('users', { filter: { q: query }, page: { limit: this.maxResults() } });

		// Prevent race conditions where a new search will finish before an old search
		if (this.state.searchQuery() !== query) return;

		this.state.currentData = data;

		this.state.loading = false;
		m.redraw();
	}

	handleUserChange(user: User | null) {
		const params = app.search.params();

		const old = params.author;

		if (!user) {
			params.author = undefined;
		} else {
			params.author = [user].map((user) => user?.slug()).join(',');
		}

		if (old !== params.author) {
			m.route.set(app.route(app.current.get('routeName'), { ...params }));
		}
	}

	get label() {
		function wrapLabel(text: Mithril.Children) {
			return app.translator.trans('glowingblue-author-filter.forum.index_page.filter_user.label', { text: <b>{text}</b> });
		}

		if (app.search.params().author) {
			const slug = app.search.params().author.split(',')[0];
			const count = app.search.params().author.split(',').length;

			const user = app.store.getBy<User>('users', 'slug', slug);

			if (!user) {
				this.handleUserChange(null);
			} else {
				return wrapLabel(user.displayName() + (count > 1 ? ` (+${count - 1})` : ''));
			}
		}

		return wrapLabel(app.translator.trans('glowingblue-author-filter.forum.index_page.filter_user.all'));
	}
}
